<template>
  <div class="bg-background-light dark:bg-background-dark text-text-main dark:text-white flex flex-col min-h-screen">
    <AppHeader />
    <main class="flex-grow">
      <div class="relative bg-background-light dark:bg-background-dark">
        <div class="container mx-auto px-4 md:px-6 lg:px-8 py-6">
          <div class="relative rounded-2xl overflow-hidden shadow-xl min-h-[400px] md:min-h-[500px] flex items-end group bg-gray-100 dark:bg-gray-800">
            <!-- Enhanced Skeleton for Carousel -->
            <div v-if="loading" class="absolute inset-0 size-full animate-pulse bg-gray-200 dark:bg-gray-700 overflow-hidden">
              <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-[shimmer_2s_infinite]"></div>
              <div class="flex flex-col items-center justify-center h-full gap-4">
                <span class="material-symbols-outlined text-7xl text-gray-300 dark:text-gray-600">image</span>
                <div class="h-1.5 w-64 bg-gray-300 dark:bg-gray-600 rounded-full"></div>
              </div>
            </div>

            <!-- Carousel Slides -->
            <transition-group name="fade" v-else>
              <div 
                v-for="(slide, index) in slides" 
                :key="slide"
                v-show="currentSlide === index"
                class="absolute inset-0 size-full bg-cover bg-center transition-all duration-1000 ease-in-out transform origin-center"
                :style="{ backgroundImage: `url(${slide})` }"
                :class="imageLoadedMap['banner-' + index] ? 'blur-0 scale-100 opacity-100' : 'blur-2xl scale-110 opacity-60'"
              >
                <img :src="slide" class="hidden" @load="onImageLoad('banner-' + index)" />
              </div>
            </transition-group>

            <!-- Gradient Overlay for Dots Readability -->
            <div class="absolute bottom-0 w-full h-24 bg-gradient-to-t from-black/50 to-transparent z-10"></div>

            <!-- Indicators -->
            <div class="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex gap-2">
              <button 
                v-for="(slide, index) in slides" 
                :key="'dot-'+index"
                @click="setSlide(index)"
                class="h-1.5 rounded-full transition-all duration-300 shadow-sm"
                :class="currentSlide === index ? 'w-8 bg-white' : 'w-4 bg-white/40 hover:bg-white/60'"
                :aria-label="'Go to slide ' + (index + 1)"
              ></button>
            </div>
          </div>
        </div>
      </div>
      <div class="bg-background-light dark:bg-background-dark py-4">
        <div class="container mx-auto px-4 md:px-6 lg:px-8">
          <div class="grid grid-cols-2 md:grid-cols-4 gap-4 -mt-6 relative z-20">
            <div class="flex flex-col md:flex-row items-center gap-4 rounded-xl border border-white/50 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-md">
              <div class="size-12 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-primary">
                <span class="material-symbols-outlined text-3xl">person_add</span>
              </div>
              <div class="text-center md:text-left">
                <h3 class="text-text-main dark:text-white text-base font-bold">入会申请</h3>
                <p class="text-xs text-text-secondary mt-1 hidden md:block">在线提交入会资料</p>
              </div>
            </div>
            <div
              class="flex flex-col md:flex-row items-center gap-4 rounded-xl border border-white/50 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-md cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all"
              role="button"
              tabindex="0"
              @click="openMediationAuth"
              @keydown.enter="openMediationAuth"
            >
              <div class="size-12 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-primary">
                <span class="material-symbols-outlined text-3xl">handshake</span>
              </div>
              <div class="text-center md:text-left">
                <h3 class="text-text-main dark:text-white text-base font-bold">调解申请</h3>
                <p class="text-xs text-text-secondary mt-1 hidden md:block">专业调解服务</p>
              </div>
            </div>
            <div class="flex flex-col md:flex-row items-center gap-4 rounded-xl border border-white/50 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-md">
              <div class="size-12 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-primary">
                <span class="material-symbols-outlined text-3xl">gavel</span>
              </div>
              <div class="text-center md:text-left">
                <h3 class="text-text-main dark:text-white text-base font-bold">法律援助</h3>
                <p class="text-xs text-text-secondary mt-1 hidden md:block">专业律师咨询服务</p>
              </div>
            </div>
            <div class="flex flex-col md:flex-row items-center gap-4 rounded-xl border border-white/50 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 shadow-md">
              <div class="size-12 rounded-full bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center text-primary">
                <span class="material-symbols-outlined text-3xl">cloud_download</span>
              </div>
              <div class="text-center md:text-left">
                <h3 class="text-text-main dark:text-white text-base font-bold">下载中心</h3>
                <p class="text-xs text-text-secondary mt-1 hidden md:block">常用表格与文件下载</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="container mx-auto px-4 md:px-6 lg:px-8 py-6">
        <div class="flex flex-col lg:flex-row gap-8 items-stretch">
          <div class="flex-1 lg:w-2/3 bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-border-color dark:border-gray-700 flex flex-col h-full">
            <div class="flex items-center justify-between border-b border-border-color dark:border-gray-700 pb-4 mb-4">
              <div class="flex gap-6">
                <button 
                  @click="activeTab = 'news'"
                  :class="activeTab === 'news' ? 'text-primary border-b-2 border-primary' : 'text-text-secondary'"
                  class="pb-4 -mb-4.5 font-bold text-lg transition-all">协会要文</button>
                <button 
                  @click="activeTab = 'notice'"
                  :class="activeTab === 'notice' ? 'text-primary border-b-2 border-primary' : 'text-text-secondary'"
                  class="pb-4 -mb-4.5 font-bold text-lg transition-all">通知公告</button>
              </div>
              <router-link 
                class="text-sm text-text-secondary hover:text-primary flex items-center" 
                :to="'/news' + (activeTab === 'notice' ? '?tab=notice' : '')">
                更多 <span class="material-symbols-outlined text-sm ml-1">arrow_forward_ios</span>
              </router-link>
            </div>
            
            <div class="flex flex-col gap-6" v-if="!loading && activeTab === 'news'">
              <!-- Headline News -->
              <div v-if="headlineNews" class="flex flex-col sm:flex-row items-stretch rounded-lg shadow-sm border border-[#e7ebf3] dark:border-gray-700 bg-white dark:bg-[#1a202c] overflow-hidden hover:shadow-md transition-all group cursor-pointer" @click="handleItemClick(headlineNews)">
                <div class="w-full sm:w-48 sm:min-w-48 h-48 sm:h-auto bg-center bg-no-repeat bg-cover transition-all duration-700 relative overflow-hidden bg-gray-100" 
                  :class="imageLoadedMap[headlineNews.id] ? 'blur-0 scale-100 opacity-100' : 'blur-xl scale-110 opacity-50'"
                  :style="headlineNews.thumbnail ? { backgroundImage: `url(${headlineNews.thumbnail})` } : {}">
                  <img v-if="headlineNews.thumbnail" :src="headlineNews.thumbnail" class="hidden" @load="onImageLoad(headlineNews.id)" />
                  <div v-if="!headlineNews.thumbnail" class="flex flex-col items-center justify-center text-gray-300 w-full h-full bg-gradient-to-br from-blue-50 to-indigo-50">
                    <span class="material-symbols-outlined text-4xl">newspaper</span>
                  </div>
                </div>
                <div class="flex flex-col justify-center p-5 gap-1 w-full">
                  <div class="flex justify-between items-start mb-1">
                    <span class="bg-red-100 text-red-600 text-xs font-bold px-2 py-0.5 rounded">头条</span>
                  </div>
                  <h3 class="text-xl md:text-2xl font-extrabold text-[#0d121b] dark:text-white leading-tight group-hover:text-primary transition-colors mb-1">
                    {{ headlineNews.title }}
                  </h3>
                  <p class="text-gray-500 dark:text-gray-400 text-sm line-clamp-2 mt-2">
                    {{ headlineNews.summary }}
                  </p>
                  <div class="flex items-center gap-4 mt-auto pt-2">
                    <div class="flex items-center gap-1 text-gray-400 text-xs">
                      <span class="material-symbols-outlined text-sm">calendar_today</span>
                      <span>{{ formatDate(headlineNews.publishTime || headlineNews.createTime) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- List News -->
              <ul class="flex flex-col gap-3">
                <li v-for="news in regularNews" :key="news.id" class="rounded-lg border border-[#e7ebf3] dark:border-gray-700 bg-white dark:bg-[#1a202c] p-4 hover:shadow-md transition-all cursor-pointer group" @click="handleItemClick(news)">
                  <div class="flex flex-col gap-1">
                    <div class="flex justify-between items-start">
                      <h3 class="text-lg font-bold text-[#0d121b] dark:text-white group-hover:text-primary transition-colors line-clamp-1">{{ news.title }}</h3>
                      <span class="text-xs text-gray-400 shrink-0 ml-4 mt-1 font-mono">{{ formatDate(news.publishTime || news.createTime) }}</span>
                    </div>
                    <p class="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">{{ news.summary }}</p>
                  </div>
                </li>
              </ul>
            </div>

            <!-- Skeleton for News List -->
            <div class="flex flex-col gap-6" v-if="loading">
              <!-- Skeleton Headline -->
              <div class="flex flex-col sm:flex-row items-stretch rounded-lg border border-gray-100 dark:border-gray-700 animate-pulse overflow-hidden">
                <div class="w-full sm:w-48 h-48 bg-gray-200 dark:bg-gray-700"></div>
                <div class="p-5 flex-1 space-y-3">
                  <div class="h-4 w-12 bg-gray-200 dark:bg-gray-700 rounded"></div>
                  <div class="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                  <div class="h-4 bg-gray-100 dark:bg-gray-800 rounded w-full"></div>
                  <div class="h-4 bg-gray-100 dark:bg-gray-800 rounded w-1/2 mt-4"></div>
                </div>
              </div>
              <!-- Skeleton List Items -->
              <div v-for="i in 3" :key="i" class="p-4 border border-gray-100 dark:border-gray-700 rounded-lg animate-pulse flex justify-between">
                <div class="space-y-2 flex-1 mr-4">
                  <div class="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                  <div class="h-4 bg-gray-100 dark:bg-gray-800 rounded w-full"></div>
                </div>
                <div class="h-4 w-20 bg-gray-100 dark:bg-gray-800 rounded"></div>
              </div>
            </div>

            <div class="flex flex-col gap-6" v-else-if="activeTab === 'notice'">
              <!-- Headline Notice (Aligned with News) -->
              <div v-if="headlineNotice" class="flex flex-col sm:flex-row items-stretch rounded-lg shadow-sm border border-[#e7ebf3] dark:border-gray-700 bg-white dark:bg-[#1a202c] overflow-hidden hover:shadow-md transition-all group cursor-pointer" @click="handleItemClick(headlineNotice)">
                <div class="w-full sm:w-48 sm:min-w-48 h-48 sm:h-auto bg-center bg-no-repeat bg-cover flex items-center justify-center overflow-hidden bg-gray-100">
                  <div class="flex flex-col items-center justify-center text-primary/40 w-full h-full bg-gradient-to-br from-blue-50 to-indigo-50">
                    <span class="material-symbols-outlined text-5xl">notifications_active</span>
                  </div>
                </div>
                <div class="flex flex-col justify-center p-5 gap-1 w-full">
                  <div class="flex justify-between items-start mb-1">
                    <span class="bg-blue-100 text-blue-600 text-xs font-bold px-2 py-0.5 rounded">重要通知</span>
                  </div>
                  <h3 class="text-xl md:text-2xl font-extrabold text-[#0d121b] dark:text-white leading-tight group-hover:text-primary transition-colors mb-1">
                    {{ headlineNotice.title }}
                  </h3>
                  <p class="text-gray-500 dark:text-gray-400 text-sm line-clamp-2 mt-2">
                    {{ headlineNotice.summary }}
                  </p>
                  <div class="flex items-center gap-4 mt-auto pt-2">
                    <div class="flex items-center gap-1 text-gray-400 text-xs">
                      <span class="material-symbols-outlined text-sm">calendar_today</span>
                      <span>{{ formatDate(headlineNotice.createTime) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- List Notice (Aligned with News) -->
              <ul class="flex flex-col gap-3">
                <li v-for="item in regularNotices" :key="item.id" class="rounded-lg border border-[#e7ebf3] dark:border-gray-700 bg-white dark:bg-[#1a202c] p-4 hover:shadow-md transition-all cursor-pointer group" @click="handleItemClick(item)">
                  <div class="flex flex-col gap-1">
                    <div class="flex justify-between items-start">
                      <h3 class="text-lg font-bold text-[#0d121b] dark:text-white group-hover:text-primary transition-colors line-clamp-1">{{ item.title }}</h3>
                      <span class="text-xs text-gray-400 shrink-0 ml-4 mt-1 font-mono">{{ formatDate(item.createTime) }}</span>
                    </div>
                    <p class="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">{{ item.summary }}</p>
                  </div>
                </li>
              </ul>
              <div v-if="notifications.length === 0" class="py-20 text-center text-gray-400">暂无通知</div>
            </div>
          </div>
          <div class="flex-1 lg:w-1/3 flex flex-col gap-3 h-full">
            <div class="bg-primary rounded-xl p-6 text-white shadow-lg relative overflow-hidden group">
              <div class="absolute right-0 top-0 opacity-10 transform translate-x-1/3 -translate-y-1/3 group-hover:scale-110 transition-transform">
                <span class="material-symbols-outlined text-[200px]">apartment</span>
              </div>
              <h3 class="text-xl font-bold mb-6 relative z-10">协会简介</h3>
              <p class="text-blue-100 text-sm leading-relaxed mb-4 relative z-10 text-justify">
                湖北省建筑劳务协会（下简称：协会）是由从事建筑劳务行业的企业自愿发起，经湖北省民政厅批准成立的全省性、行业性、非盈利性社会团体。湖北省建筑劳务协会于2018年8月9日正式登记成立，在往届领导班子的带领下，在会员单位的参与和支持下，目前已发展有429家建筑劳务会员单位。
              </p>
              <router-link to="/about" class="inline-flex items-center text-sm font-bold hover:underline relative z-10">
                查看详细介绍 <span class="material-symbols-outlined text-sm ml-1">arrow_forward</span>
              </router-link>
            </div>
            <div class="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-border-color dark:border-gray-700 flex-1 flex flex-col">
              <h3 class="text-lg font-bold text-text-main dark:text-white mb-4 flex items-center gap-2">
                <span class="w-1 h-6 bg-primary rounded-full"></span> 查询服务
              </h3>
              <div class="grid grid-cols-2 gap-4 flex-1">
                <a class="flex flex-col items-center justify-center p-4 bg-background-light dark:bg-gray-700 rounded-lg hover:bg-blue-50 dark:hover:bg-gray-600 transition-colors h-full" href="#">
                  <span class="material-symbols-outlined text-primary text-3xl mb-2">policy</span>
                  <span class="text-sm font-medium dark:text-gray-200">政策查询</span>
                </a>
                <a class="flex flex-col items-center justify-center p-4 bg-background-light dark:bg-gray-700 rounded-lg hover:bg-blue-50 dark:hover:bg-gray-600 transition-colors h-full" href="#">
                  <span class="material-symbols-outlined text-primary text-3xl mb-2">engineering</span>
                  <span class="text-sm font-medium dark:text-gray-200">人物风采</span>
                </a>
                <a class="flex flex-col items-center justify-center p-4 bg-background-light dark:bg-gray-700 rounded-lg hover:bg-blue-50 dark:hover:bg-gray-600 transition-colors h-full" href="#">
                  <span class="material-symbols-outlined text-primary text-3xl mb-2">verified_user</span>
                  <span class="text-sm font-medium dark:text-gray-200">诚信查询</span>
                </a>
                <a class="flex flex-col items-center justify-center p-4 bg-background-light dark:bg-gray-700 rounded-lg hover:bg-blue-50 dark:hover:bg-gray-600 transition-colors h-full" href="#">
                  <span class="material-symbols-outlined text-primary text-3xl mb-2">contact_support</span>
                  <span class="text-sm font-medium dark:text-gray-200">投诉建议</span>
                </a>
              </div>
            </div>

          </div>
        </div>
      </div>

    </main>
    <footer class="bg-[#0b1019] text-white pt-16 pb-8">
      <div class="container mx-auto px-4 md:px-6 lg:px-8">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          <div class="col-span-1 lg:col-span-1">
            <h3 class="text-lg font-bold mb-6 flex items-center gap-2">
              <img alt="Logo" class="w-8 h-8 object-contain" src="/logo.png"/>
              湖北省建筑劳务协会
            </h3>
            <p class="text-gray-400 text-sm leading-relaxed mb-6">
              弘扬工匠精神，助推行业发展!<br/>
              ——为建筑劳务保驾护航!
            </p>
            <div class="flex gap-4">
              <a class="size-8 rounded-full bg-gray-800 flex items-center justify-center hover:bg-primary transition-colors text-white" href="#">
                <span class="material-symbols-outlined text-sm">chat</span>
              </a>
              <a class="size-8 rounded-full bg-gray-800 flex items-center justify-center hover:bg-primary transition-colors text-white" href="#">
                <span class="material-symbols-outlined text-sm">mail</span>
              </a>
            </div>
          </div>
          <div>
            <h4 class="text-base font-bold mb-6 text-gray-200">快速链接</h4>
            <ul class="space-y-3 text-sm text-gray-400">
              <li><a class="hover:text-primary transition-colors" href="https://zjt.hubei.gov.cn/" target="_blank">湖北省住建厅官网</a></li>
            </ul>
          </div>
          <div>
            <h4 class="text-base font-bold mb-6 text-gray-200">联系我们</h4>
            <ul class="space-y-4 text-sm text-gray-400">
              <li class="flex items-start gap-3">
                <span class="material-symbols-outlined text-primary text-lg mt-0.5">location_on</span>
                <span>武汉市汉阳区龙阳大道123号<br/>欧亚达汉阳国际广场A座1803</span>
              </li>
              <li class="flex items-center gap-3">
                <span class="material-symbols-outlined text-primary text-lg">call</span>
                <span>027-84881278 (电话/传真)</span>
              </li>
              <li class="flex items-center gap-3">
                <span class="material-symbols-outlined text-primary text-lg">mail</span>
                <span>hbsjzlwxh@163.com</span>
              </li>
              <li class="flex items-center gap-3">
                <span class="material-symbols-outlined text-primary text-lg">markunread_mailbox</span>
                <span>430000</span>
              </li>
            </ul>
          </div>
          <div>
            <h4 class="text-base font-bold mb-6 text-gray-200">关注我们</h4>
            <div class="flex flex-row gap-4">
              <div class="flex flex-col items-center">
                <div class="bg-white p-2 w-24 h-24 rounded-lg flex items-center justify-center">
                  <img src="/images/wechat_qrcode.jpg" class="w-full h-full object-contain" alt="微信公众号">
                </div>
                <p class="text-[10px] text-gray-500 mt-2 text-center">官方微信公众号</p>
              </div>
              <div class="flex flex-col items-center">
                <div class="bg-white p-2 w-24 h-24 rounded-lg flex items-center justify-center">
                  <img src="/images/video_account_qrcode.png" class="w-full h-full object-contain" alt="视频号">
                </div>
                <p class="text-[10px] text-gray-500 mt-2 text-center">官方视频号</p>
              </div>
            </div>
            <p class="text-xs text-gray-500 mt-4">扫码关注获取最新动态</p>
          </div>
        </div>
        <div class="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center text-xs text-gray-500 gap-4">
          <p>©2018 湖北省建筑劳务协会保留所有权利。</p>
          <div class="flex gap-4">
            <a class="hover:text-gray-300" href="https://beian.miit.gov.cn/" target="_blank">鄂ICP备18023234号-1</a>
            <span>|</span>
            <a class="hover:text-gray-300" href="#">隐私政策</a>
            <span>|</span>
            <a class="hover:text-gray-300" href="#">网站地图</a>
          </div>
        </div>
      </div>
    </footer>

    <!-- 微信扫码授权弹窗：调解申请（与会员申请/在线留言同款扫码基建） -->
    <WxQrAuthDialog v-model="showMediationAuth" biz="mediationApply" @success="onMediationAuthSuccess" />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue';
import WxQrAuthDialog from '@/components/WxQrAuthDialog.vue';
import newsService from '@/api/newsService';
import bannerService from '@/api/bannerService';
import dayjs from 'dayjs';

export default {
  name: 'HomeView',
  components: {
    AppHeader,
    WxQrAuthDialog
  },
  data() {
    return {
      currentSlide: 0,
      timer: null,
      slides: [],
      newsList: [],
      notifications: [],
      activeTab: 'news', // news, industry, notice
      loading: true, // 初始加载设为 true
      imageLoadedMap: {}, // 记录图片加载状态
      showMediationAuth: false // 调解申请：微信扫码授权弹窗
    }
  },
  computed: {
    headlineNews() {
      return this.newsList.find(n => n.isHeadline) || this.newsList[0];
    },
    regularNews() {
      const headlineId = this.headlineNews?.id;
      return this.newsList.filter(n => n.id !== headlineId).slice(0, 3);
    },
    processedNotifications() {
      return this.notifications.map(item => ({
        ...item,
        summary: item.content ? item.content.replace(/<[^>]+>/g, '').substring(0, 50) + '...' : ''
      }));
    },
    headlineNotice() {
      return this.processedNotifications[0];
    },
    regularNotices() {
      return this.processedNotifications.slice(1, 4);
    }
  },
  async mounted() {
    this.startSlideTimer();
    this.fetchData();
  },
  beforeUnmount() {
    this.stopSlideTimer();
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        const [newsRes, notifRes, bannerRes] = await Promise.all([
          newsService.getNewsList('association'),
          newsService.getNotifications(),
          bannerService.getActiveBanners()
        ]);
        this.newsList = newsRes.data || newsRes || [];
        this.notifications = notifRes.data || notifRes || [];
        const banners = bannerRes.data || bannerRes || [];
        this.slides = banners.map(b => b.imageUrl);
      } catch (error) {
        console.error('获取首页数据失败:', error);
      } finally {
        this.loading = false;
      }
    },
    handleItemClick(item) {
      const link = this.getLink(item);
      const target = this.getTarget(item);
      if (target === '_blank') {
        window.open(link, '_blank');
      } else {
        this.$router.push(link);
      }
    },
    onImageLoad(id) {
      this.imageLoadedMap[id] = true;
    },
    formatDate(date) {
      return date ? dayjs(date).format('YYYY-MM-DD') : '-';
    },
    getLink(item) {
      if (item.linkType === 1 || item.externalUrl) return item.externalUrl;
      if (item.linkUrl) return item.linkUrl;
      
      const isNews = Object.prototype.hasOwnProperty.call(item, 'categoryId');
      const type = isNews ? 'news' : 'notification';
      return `/${type}/detail/${item.id}`;
    },
    getTarget(item) {
      return (item.linkType === 1 || item.externalUrl || item.linkUrl) ? '_blank' : '_self';
    },
    startSlideTimer() {
      this.timer = setInterval(this.nextSlide, 5000);
    },
    stopSlideTimer() {
      if (this.timer) {
        clearInterval(this.timer);
        this.timer = null;
      }
    },
    nextSlide() {
      this.currentSlide = (this.currentSlide + 1) % this.slides.length;
    },
    prevSlide() {
      this.currentSlide = (this.currentSlide - 1 + this.slides.length) % this.slides.length;
    },
    setSlide(index) {
      this.currentSlide = index;
      this.stopSlideTimer();
      this.startSlideTimer();
    },
    goToLogin() {
      this.$router.push('/login');
    },
    /** 首页「调解申请」服务卡：先完成微信扫码授权，再进 web 端表单页 */
    openMediationAuth() {
      this.showMediationAuth = true;
    },
    onMediationAuthSuccess() {
      // 授权态已写入 applyAuth（独立于管理端登录态），进入 web 端调解申请表单页
      this.$router.push('/mediation-apply');
    }
  }
}
</script>

<style scoped>
/* Carousel Fade Transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s ease-in-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes shimmer {
  100% {
    transform: translateX(100%);
  }
}
</style>
